// src/pages/buyer/ReferralPage.jsx
// Buyer referral program hub — share code, track referrals, earn credits
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { referralApi } from '../../api/services'
import { PageSpinner, Card, CardHeader, CardBody } from '../../components/common/ui'
import { Gift, Users, ShoppingCart, Wallet, Copy, Link2, Share2 } from 'lucide-react'
import toast from 'react-hot-toast'

function StatCard({ icon, label, value, sub }) {
  return (
    <Card>
      <CardBody className="text-center py-5">
        <div className="text-2xl mb-2">{icon}</div>
        <div className="text-2xl font-display font-bold">{value}</div>
        <div className="text-sm font-bold text-gray-700 mt-1">{label}</div>
        {sub && <div className="text-xs text-muted mt-1">{sub}</div>}
      </CardBody>
    </Card>
  )
}

const STATUS_COLORS = {
  signed_up: 'bg-purple-50 text-purple-700',
  converted: 'bg-green-50 text-green-700',
  rewarded:  'bg-orange-50 text-orange-700',
  expired:   'bg-gray-100 text-gray-500',
}

export default function ReferralPage() {
  const [copied, setCopied] = useState(false)

  const { data: codeData, isLoading: loadingCode } = useQuery({
    queryKey: ['referral-code'],
    queryFn: () => referralApi.myCode().then(r => r.data?.data || r.data),
  })

  const { data: referrals, isLoading: loadingReferrals } = useQuery({
    queryKey: ['referral-list'],
    queryFn: () => referralApi.myReferrals().then(r => {
      const d = r.data?.data || r.data
      return Array.isArray(d) ? d : []
    }),
  })

  const { data: stats } = useQuery({
    queryKey: ['referral-stats'],
    queryFn: () => referralApi.stats().then(r => r.data?.data || r.data),
  })

  const code = codeData

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code?.code ?? '')
      setCopied(true)
      toast.success('Code copied!')
      setTimeout(() => setCopied(false), 2000)
    } catch { /* ignore */ }
  }

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(code?.share_url ?? '')
      toast.success('Link copied!')
    } catch { /* ignore */ }
  }

  const shareWhatsApp = () => {
    const msg = encodeURIComponent(`Join Fabrinza — India's premium textile marketplace! Use my referral code *${code?.code}* to get ₹500 off your first order. Sign up: ${code?.share_url}`)
    window.open(`https://wa.me/?text=${msg}`, '_blank')
  }

  if (loadingCode || loadingReferrals) return <PageSpinner />

  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-display font-bold mb-2">Refer & Earn</h1>
      <p className="text-sm text-muted mb-6">Invite buyers to Fabrinza and earn ₹500 for every first order they place</p>

      {/* How it works */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          [<Gift size={20} className="text-pink" />, 'Share Your Code', 'Send your unique referral code or link'],
          [<Users size={20} className="text-pink" />, 'They Sign Up', 'Friend registers with your code'],
          [<Wallet size={20} className="text-pink" />, 'Both Earn', 'You get ₹500 · They get ₹500 off'],
        ].map(([icon, title, desc], i) => (
          <div key={i} className="bg-pink-light border border-pink/10 rounded-2xl p-4 text-center">
            <div className="flex justify-center mb-2">{icon}</div>
            <div className="font-bold text-sm text-gray-900 mb-1">{title}</div>
            <div className="text-xs text-muted">{desc}</div>
          </div>
        ))}
      </div>

      {/* Code card */}
      {code && (
        <div className="bg-gradient-brand rounded-2xl p-6 mb-6 text-white relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-36 h-36 rounded-full bg-white/10" />
          <div className="absolute right-8 -bottom-12 w-44 h-44 rounded-full bg-white/5" />
          <p className="text-sm font-medium opacity-80 mb-2">Your Referral Code</p>
          <p className="font-display text-3xl font-black tracking-widest mb-4">{code.code}</p>
          <div className="flex gap-2 flex-wrap relative z-10">
            <button onClick={copyCode}
              className="px-4 py-2 rounded-xl border-2 border-white/40 bg-white/15 text-white text-sm font-bold hover:bg-white/25 transition flex items-center gap-1.5">
              <Copy size={14} /> {copied ? 'Copied!' : 'Copy Code'}
            </button>
            <button onClick={copyUrl}
              className="px-4 py-2 rounded-xl border-2 border-white/40 bg-white/15 text-white text-sm font-bold hover:bg-white/25 transition flex items-center gap-1.5">
              <Link2 size={14} /> Copy Link
            </button>
            <button onClick={shareWhatsApp}
              className="px-4 py-2 rounded-xl bg-[#25D366] text-white text-sm font-bold hover:bg-[#20bd5a] transition flex items-center gap-1.5">
              <Share2 size={14} /> WhatsApp
            </button>
          </div>
          <p className="text-xs opacity-60 mt-3 break-all">{code.share_url}</p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <StatCard icon={<Users size={22} className="text-gray-600 mx-auto" />} label="Total Signups" value={code?.total_signups ?? 0} />
        <StatCard icon={<ShoppingCart size={22} className="text-pink mx-auto" />} label="Conversions" value={code?.total_conversions ?? 0} sub="placed first order" />
        <StatCard icon={<Wallet size={22} className="text-orange mx-auto" />} label="Rewards Earned"
          value={`₹${Number(code?.total_reward_inr ?? 0).toLocaleString('en-IN')}`} sub="total credits" />
      </div>

      {/* Platform stats */}
      {stats && (
        <div className="flex items-center justify-between bg-white border border-border rounded-2xl px-5 py-3 mb-6 text-sm">
          <span className="text-muted">Platform conversion rate: <strong className="text-gray-900">{stats.conversion_rate ?? 0}%</strong></span>
          <span className="text-muted">Total rewards issued: <strong className="text-pink">₹{Number(stats.total_rewards_inr ?? 0).toLocaleString('en-IN')}</strong></span>
        </div>
      )}

      {/* Referral history */}
      <Card>
        <CardHeader><span className="font-bold text-sm">Referral History</span></CardHeader>
        <CardBody className="p-0">
          {(!referrals || referrals.length === 0) ? (
            <div className="py-10 text-center text-muted">
              <p className="text-2xl mb-2">📭</p>
              <p className="text-sm">No referrals yet — start sharing your code!</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface">
                  {['Friend', 'Joined', 'Status', 'Your Reward'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold text-muted uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {referrals.map((r, i) => (
                  <tr key={i} className="border-b border-border last:border-0 hover:bg-surface/50">
                    <td className="px-4 py-3 font-medium">{r.referred_name ?? 'Anonymous'}</td>
                    <td className="px-4 py-3 text-muted">{r.referred_at}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_COLORS[r.status] || STATUS_COLORS.expired}`}>
                        {r.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold">
                      {r.reward ? <span className="text-orange">₹{Number(r.reward).toLocaleString('en-IN')}</span> : <span className="text-muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <p className="mt-4 text-xs text-muted bg-surface rounded-xl px-4 py-3">
        Rewards are credited as Fabrinza wallet credits after your referral places their first order of ₹2,000 or more. Credits expire in 12 months.
      </p>
    </div>
  )
}
